import React, { useEffect, useRef, useState, useCallback } from 'react';
import logoDark from '../images/company_logo.png';
import logoLight from '../images/company_logo_dark.png';
import { useThemeMode } from '../contexts/ThemeContext';

// ─── Alphanetix Splash — Integration-Pipeline Convergence ───────
// A full-screen canvas animation:
//   Phase 1 (0-1.8s)  : Scattered nodes drift in from edges, connection lines form
//   Phase 2 (1.8-3.2s): Data packets pulse along the connections toward center
//   Phase 3 (3.2-4.2s): Nodes converge into a hexagonal core, wordmark fades in
//   Phase 4 (4.2-5.0s): Everything settles, glow pulse, then fade-out
//
// Respects prefers-reduced-motion — skips straight to wordmark.
//
// Adaptive timing: accepts an `appReady` prop. Once the app signals
// readiness AND the minimum display time has elapsed, the splash
// fast-forwards to the logo reveal and fades out — no wasted seconds.

const TOTAL_DURATION = 4800; // ms — full animation length (fallback)
const MIN_DISPLAY = 1800;   // ms — minimum time before early dismiss
const FADE_OUT = 600;

/* ── helpers ─────────────────────────────────────────────────── */
const lerp = (a, b, t) => a + (b - a) * t;
const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);
const easeInOutQuart = (t) =>
  t < 0.5 ? 8 * t * t * t * t : 1 - Math.pow(-2 * t + 2, 4) / 2;

/* ── node / edge seed layouts ────────────────────────────────── */
// 12 nodes start off-screen and converge to a hex-ring layout around center
const HEX_RADIUS = 90;
const NODE_COUNT = 12;

const buildNodes = (cx, cy, hueBase = 230, hueRange = 50) => {
  const nodes = [];
  for (let i = 0; i < NODE_COUNT; i++) {
    const angle = (Math.PI * 2 * i) / NODE_COUNT;
    // target position — hex ring around center
    const tx = cx + Math.cos(angle) * HEX_RADIUS;
    const ty = cy + Math.sin(angle) * HEX_RADIUS;
    // start position — scattered far offscreen from the same angle
    const dist = 500 + Math.random() * 300;
    const sx = cx + Math.cos(angle) * dist;
    const sy = cy + Math.sin(angle) * dist;
    nodes.push({
      sx, sy, tx, ty,
      x: sx, y: sy,
      radius: 3 + Math.random() * 2,
      hue: hueBase + Math.random() * hueRange,
      delay: Math.random() * 0.3,
    });
  }
  return nodes;
};

// connections: ring edges + some cross-links
const buildEdges = () => {
  const edges = [];
  for (let i = 0; i < NODE_COUNT; i++) {
    edges.push([i, (i + 1) % NODE_COUNT]);
    if (i % 3 === 0) edges.push([i, (i + 4) % NODE_COUNT]);
    if (i % 4 === 0) edges.push([i, (i + 6) % NODE_COUNT]);
  }
  return edges;
};

// packets — travel along edges toward center
const buildPackets = (edges) =>
  edges.map((_, i) => ({
    edgeIdx: i,
    progress: 0,
    speed: 0.006 + Math.random() * 0.006,
    size: 1.2 + Math.random() * 1,
    active: false,
  }));

/* ══════════════════════════════════════════════════════════════ */
export default function SplashScreen({ onFinished, appReady = false }) {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const [fading, setFading] = useState(false);
  const [visible, setVisible] = useState(true);
  const dismissedRef = useRef(false);
  const startTimeRef = useRef(Date.now());
  const [loadProgress, setLoadProgress] = useState(0);
  const [statusIndex, setStatusIndex] = useState(0);
  const [isReady, setIsReady] = useState(false);
  const { isLight } = useThemeMode();

  // Loading status messages
  const statusMessages = [
    'Initializing modules',
    'Loading components',
    'Preparing interface',
    'Almost there',
  ];

  // Theme-aware palette
  const palette = isLight
    ? {
        bg: 'radial-gradient(ellipse at center, #FEF9EE 0%, #FAFAF9 70%)',
        bgSolid: '#FAFAF9',
        lineColor: (a) => `rgba(99, 102, 241, ${a})`,
        packetColor: (a) => `rgba(99, 102, 241, ${a})`,
        packetGlow: (a) => `rgba(251, 191, 36, ${a})`,
        nodeHueBase: 240,
        nodeHueRange: 30,
        nodeSat: '70%',
        nodeLit: '55%',
        coreGlow1: (a) => `rgba(99, 102, 241, ${a})`,
        coreGlow2: (a) => `rgba(251, 191, 36, ${a})`,
        logoGlow: 'rgba(99, 102, 241, 0.35)',
        tagline: (a) => `rgba(71, 85, 105, ${a})`,
        skipBorder: 'rgba(100, 116, 139, 0.3)',
        skipColor: 'rgba(100, 116, 139, 0.6)',
        skipHoverColor: 'rgba(30, 41, 59, 0.9)',
        skipHoverBorder: 'rgba(100, 116, 139, 0.5)',
      }
    : {
        bg: 'radial-gradient(ellipse at center, #0f172a 0%, #020617 70%)',
        bgSolid: '#020617',
        lineColor: (a) => `rgba(129, 140, 248, ${a})`,
        packetColor: (a) => `rgba(16, 185, 129, ${a})`,
        packetGlow: (a) => `rgba(16, 185, 129, ${a})`,
        nodeHueBase: 230,
        nodeHueRange: 50,
        nodeSat: '80%',
        nodeLit: '70%',
        coreGlow1: (a) => `rgba(99, 102, 241, ${a})`,
        coreGlow2: (a) => `rgba(16, 185, 129, ${a})`,
        logoGlow: 'rgba(99, 102, 241, 0.5)',
        tagline: (a) => `rgba(148, 163, 184, ${a})`,
        skipBorder: 'rgba(148, 163, 184, 0.25)',
        skipColor: 'rgba(148, 163, 184, 0.6)',
        skipHoverColor: 'rgba(224, 231, 255, 0.9)',
        skipHoverBorder: 'rgba(148, 163, 184, 0.5)',
      };

  // Simulated progress — advances in stages, snaps to 100% on appReady
  useEffect(() => {
    if (isReady) return;
    const stages = [
      { at: 300,  to: 15 },
      { at: 800,  to: 30 },
      { at: 1400, to: 50 },
      { at: 2200, to: 65 },
      { at: 3000, to: 78 },
      { at: 3800, to: 88 },
    ];
    const timers = stages.map(({ at, to }) =>
      setTimeout(() => setLoadProgress(prev => Math.max(prev, to)), at)
    );
    return () => timers.forEach(clearTimeout);
  }, [isReady]);

  // Cycle status messages
  useEffect(() => {
    if (isReady) return;
    const interval = setInterval(() => {
      setStatusIndex(prev => (prev + 1) % statusMessages.length);
    }, 1200);
    return () => clearInterval(interval);
  }, [isReady, statusMessages.length]);

  // When appReady fires, snap progress to 100% and show "Ready"
  useEffect(() => {
    if (appReady && !isReady) {
      setIsReady(true);
      setLoadProgress(100);
    }
  }, [appReady, isReady]);

  // Detect reduced-motion preference
  const prefersReducedMotion =
    typeof window !== 'undefined' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // Pre-load the logo and create a tinted version for canvas
  const processedLogo = useRef(null);

  useEffect(() => {
    const img = new Image();
    img.onload = () => {
      const oc = document.createElement('canvas');
      oc.width = img.width;
      oc.height = img.height;
      const octx = oc.getContext('2d');
      octx.drawImage(img, 0, 0);
      if (!isLight) {
        // Dark mode: tint all pixels white, preserving alpha
        octx.globalCompositeOperation = 'source-in';
        octx.fillStyle = '#ffffff';
        octx.fillRect(0, 0, oc.width, oc.height);
      }
      // Light mode: use the dark logo as-is
      processedLogo.current = oc;
    };
    img.src = isLight ? logoLight : logoDark;
  }, [isLight]);

  const animate = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const dpr = window.devicePixelRatio || 1;
    const W = canvas.width / dpr;
    const H = canvas.height / dpr;
    const cx = W / 2;
    const cy = H / 2;

    const nodes = buildNodes(cx, cy, palette.nodeHueBase, palette.nodeHueRange);
    const edges = buildEdges();
    const packets = buildPackets(edges);

    const startTime = performance.now();
    let raf;

    const tick = (now) => {
      const elapsed = now - startTime;
      const t = Math.min(elapsed / TOTAL_DURATION, 1); // 0→1 over full duration

      ctx.clearRect(0, 0, W, H);

      // ── Phase progress (overlapping) ──
      const tConverge = Math.min(t / 0.45, 1);        // 0-45%: nodes fly in
      const tConnect  = Math.max(0, Math.min((t - 0.2) / 0.3, 1)); // 20-50%: lines appear
      const tPackets  = Math.max(0, Math.min((t - 0.35) / 0.35, 1)); // 35-70%: packets flow
      const tSettle   = Math.max(0, Math.min((t - 0.65) / 0.2, 1));  // 65-85%: settle
      const tWordmark = Math.max(0, Math.min((t - 0.72) / 0.2, 1)); // 72-92%: wordmark
      const tGlow     = Math.max(0, Math.min((t - 0.85) / 0.15, 1)); // 85-100%: glow pulse

      // ── Update node positions ──
      nodes.forEach((n) => {
        const d = Math.max(0, Math.min((tConverge - n.delay) / (1 - n.delay), 1));
        const e = easeOutCubic(d);
        n.x = lerp(n.sx, n.tx, e);
        n.y = lerp(n.sy, n.ty, e);
      });

      // ── Draw connections ──
      if (tConnect > 0) {
        const lineAlpha = tConnect * 0.35;
        edges.forEach(([a, b]) => {
          ctx.beginPath();
          ctx.moveTo(nodes[a].x, nodes[a].y);
          ctx.lineTo(nodes[b].x, nodes[b].y);
          ctx.strokeStyle = palette.lineColor(lineAlpha);
          ctx.lineWidth = 1;
          ctx.stroke();
        });
      }

      // ── Draw packets ──
      if (tPackets > 0) {
        packets.forEach((p) => {
          if (!p.active && tPackets > 0.1) p.active = true;
          if (!p.active) return;
          p.progress = (p.progress + p.speed) % 1;
          const [a, b] = edges[p.edgeIdx];
          const px = lerp(nodes[a].x, nodes[b].x, p.progress);
          const py = lerp(nodes[a].y, nodes[b].y, p.progress);
          ctx.beginPath();
          ctx.arc(px, py, p.size, 0, Math.PI * 2);
          ctx.fillStyle = palette.packetColor(0.35 * tPackets);
          ctx.fill();
          // packet glow
          ctx.beginPath();
          ctx.arc(px, py, p.size * 2.5, 0, Math.PI * 2);
          ctx.fillStyle = palette.packetGlow(0.06 * tPackets);
          ctx.fill();
        });
      }

      // ── Draw nodes ──
      nodes.forEach((n) => {
        // Outer glow
        const glow = ctx.createRadialGradient(n.x, n.y, 0, n.x, n.y, n.radius * 4);
        glow.addColorStop(0, `hsla(${n.hue}, ${palette.nodeSat}, ${palette.nodeLit}, 0.25)`);
        glow.addColorStop(1, `hsla(${n.hue}, ${palette.nodeSat}, ${palette.nodeLit}, 0)`);
        ctx.beginPath();
        ctx.arc(n.x, n.y, n.radius * 4, 0, Math.PI * 2);
        ctx.fillStyle = glow;
        ctx.fill();

        // Core dot
        ctx.beginPath();
        ctx.arc(n.x, n.y, n.radius, 0, Math.PI * 2);
        ctx.fillStyle = `hsla(${n.hue}, ${palette.nodeSat}, ${palette.nodeLit}, ${0.6 + tSettle * 0.4})`;
        ctx.fill();
      });

      // ── Central glow ──
      if (tSettle > 0) {
        const pulse = 1 + Math.sin(elapsed * 0.003) * 0.15 * tGlow;
        const coreGlow = ctx.createRadialGradient(cx, cy, 0, cx, cy, 80 * pulse);
        coreGlow.addColorStop(0, palette.coreGlow1(0.18 * tSettle));
        coreGlow.addColorStop(0.5, palette.coreGlow2(0.08 * tSettle));
        coreGlow.addColorStop(1, 'rgba(0, 0, 0, 0)');
        ctx.beginPath();
        ctx.arc(cx, cy, 80 * pulse, 0, Math.PI * 2);
        ctx.fillStyle = coreGlow;
        ctx.fill();
      }

      // ── Logo + tagline ──
      if (tWordmark > 0) {
        const wordAlpha = easeInOutQuart(tWordmark);
        ctx.save();
        ctx.globalAlpha = wordAlpha;

        // Draw logo centered
        if (processedLogo.current) {
          const wl = processedLogo.current;
          const maxW = Math.min(220, W * 0.4);
          const scale = maxW / wl.width;
          const drawW = wl.width * scale;
          const drawH = wl.height * scale;

          // Logo glow
          ctx.shadowColor = palette.logoGlow;
          ctx.shadowBlur = 25;
          ctx.drawImage(wl, cx - drawW / 2, cy - drawH / 2 - 8, drawW, drawH);
          ctx.shadowBlur = 0;
        }

        // Tagline below the logo
        ctx.font = '400 11px Inter, system-ui, sans-serif';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillStyle = palette.tagline(wordAlpha);
        ctx.fillText('Enterprise Integration Platform', cx, cy + 40);

        ctx.restore();
      }

      if (elapsed < TOTAL_DURATION + 200) {
        raf = requestAnimationFrame(tick);
      }
    };

    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [palette]);

  // Dismiss helper — triggers fade-out exactly once
  const dismiss = useCallback(() => {
    if (dismissedRef.current) return;
    dismissedRef.current = true;
    setFading(true);
    setTimeout(() => {
      setVisible(false);
      onFinished?.();
    }, FADE_OUT);
  }, [onFinished]);

  // Adaptive early dismiss: once appReady AND min time elapsed
  useEffect(() => {
    if (!appReady || dismissedRef.current) return;
    const elapsed = Date.now() - startTimeRef.current;
    const remaining = Math.max(0, MIN_DISPLAY - elapsed);
    const timer = setTimeout(dismiss, remaining);
    return () => clearTimeout(timer);
  }, [appReady, dismiss]);

  // Resize canvas + kick off animation
  useEffect(() => {
    if (prefersReducedMotion) {
      // Skip animation entirely — dismiss as soon as app is ready (or after brief delay)
      const timer = setTimeout(() => dismiss(), appReady ? 0 : 600);
      return () => clearTimeout(timer);
    }

    const canvas = canvasRef.current;
    if (!canvas) return;

    const resize = () => {
      const dpr = window.devicePixelRatio || 1;
      canvas.width = window.innerWidth * dpr;
      canvas.height = window.innerHeight * dpr;
      canvas.style.width = window.innerWidth + 'px';
      canvas.style.height = window.innerHeight + 'px';
      canvas.getContext('2d').scale(dpr, dpr);
    };
    resize();
    window.addEventListener('resize', resize);

    const cleanup = animate();

    // Hard cap: always dismiss after full animation even if appReady never fires
    const maxTimer = setTimeout(dismiss, TOTAL_DURATION);

    return () => {
      cleanup?.();
      window.removeEventListener('resize', resize);
      clearTimeout(maxTimer);
    };
  }, [animate, dismiss, prefersReducedMotion, appReady]);

  if (!visible) return null;

  // Reduced motion fallback — simple wordmark + fade
  if (prefersReducedMotion) {
    return (
      <div
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 99999,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          background: palette.bgSolid,
          opacity: fading ? 0 : 1,
          transition: 'opacity 0.5s ease',
          pointerEvents: fading ? 'none' : 'auto',
        }}
      >
        <img
          src={isLight ? logoLight : logoDark}
          alt="Alphanetix"
          style={{ width: 200, maxWidth: '40vw', filter: isLight ? 'none' : 'brightness(0) invert(1)', objectFit: 'contain' }}
        />
        <div style={{ color: isLight ? '#475569' : '#64748b', fontSize: 11, marginTop: 12, fontFamily: 'Inter, system-ui' }}>
          Enterprise Integration Platform
        </div>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 99999,
        background: palette.bg,
        opacity: fading ? 0 : 1,
        transition: `opacity ${FADE_OUT}ms ease`,
        cursor: 'default',
        userSelect: 'none',
        pointerEvents: fading ? 'none' : 'auto',
      }}
    >
      <canvas
        ref={canvasRef}
        style={{
          display: 'block',
          width: '100%',
          height: '100%',
        }}
      />
      {/* Loading status indicator */}
      <div
        style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          paddingBottom: 72,
          zIndex: 2,
        }}
      >
        {/* Status label */}
        <div
          style={{
            fontSize: 11,
            fontWeight: 500,
            fontFamily: 'Inter, system-ui, sans-serif',
            letterSpacing: '1.5px',
            textTransform: 'uppercase',
            color: isReady
              ? (isLight ? '#6366f1' : '#10b981')
              : (isLight ? 'rgba(71, 85, 105, 0.5)' : 'rgba(148, 163, 184, 0.5)'),
            marginBottom: 12,
            transition: 'color 0.4s ease',
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            minHeight: 16,
          }}
        >
          {isReady ? (
            <>
              <span
                style={{
                  display: 'inline-block',
                  width: 14,
                  height: 14,
                  borderRadius: '50%',
                  background: isLight ? '#6366f1' : '#10b981',
                  position: 'relative',
                  animation: 'readyPop 0.35s cubic-bezier(0.34, 1.56, 0.64, 1) forwards',
                }}
              >
                <svg
                  viewBox="0 0 16 16"
                  style={{
                    position: 'absolute',
                    top: 2,
                    left: 2,
                    width: 10,
                    height: 10,
                  }}
                >
                  <path
                    d="M3 8l3 3 5-6"
                    fill="none"
                    stroke="#fff"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    style={{
                      strokeDasharray: 20,
                      strokeDashoffset: 20,
                      animation: 'checkDraw 0.3s 0.15s ease forwards',
                    }}
                  />
                </svg>
              </span>
              Ready
            </>
          ) : (
            <>
              {/* Animated dots */}
              <span style={{ display: 'inline-flex', gap: 3 }}>
                {[0, 1, 2].map(i => (
                  <span
                    key={i}
                    style={{
                      width: 3,
                      height: 3,
                      borderRadius: '50%',
                      background: isLight ? 'rgba(99, 102, 241, 0.4)' : 'rgba(16, 185, 129, 0.4)',
                      animation: `dotPulse 1.2s ${i * 0.2}s ease-in-out infinite`,
                    }}
                  />
                ))}
              </span>
              <span
                key={statusIndex}
                style={{
                  animation: 'statusFade 0.4s ease',
                }}
              >
                {statusMessages[statusIndex]}
              </span>
            </>
          )}
        </div>

        {/* Progress track */}
        <div
          style={{
            width: 160,
            height: 2,
            borderRadius: 1,
            background: isLight ? 'rgba(99, 102, 241, 0.1)' : 'rgba(148, 163, 184, 0.1)',
            overflow: 'hidden',
            position: 'relative',
          }}
        >
          {/* Progress fill */}
          <div
            style={{
              height: '100%',
              width: `${loadProgress}%`,
              borderRadius: 1,
              background: isReady
                ? (isLight ? '#6366f1' : '#10b981')
                : (isLight
                    ? 'linear-gradient(90deg, rgba(99, 102, 241, 0.3), rgba(99, 102, 241, 0.6))'
                    : 'linear-gradient(90deg, rgba(16, 185, 129, 0.3), rgba(16, 185, 129, 0.6))'),
              transition: isReady ? 'width 0.4s ease, background 0.3s ease' : 'width 0.8s cubic-bezier(0.4, 0, 0.2, 1)',
              boxShadow: isReady
                ? (isLight ? '0 0 8px rgba(99, 102, 241, 0.5)' : '0 0 8px rgba(16, 185, 129, 0.5)')
                : 'none',
            }}
          />
          {/* Shimmer effect while loading */}
          {!isReady && (
            <div
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: isLight
                  ? 'linear-gradient(90deg, transparent 0%, rgba(99, 102, 241, 0.15) 50%, transparent 100%)'
                  : 'linear-gradient(90deg, transparent 0%, rgba(16, 185, 129, 0.15) 50%, transparent 100%)',
                backgroundSize: '200% 100%',
                animation: 'shimmer 1.5s ease-in-out infinite',
              }}
            />
          )}
        </div>
      </div>

      {/* CSS animations for status indicator */}
      <style>{`
        @keyframes dotPulse {
          0%, 100% { opacity: 0.3; transform: scale(1); }
          50% { opacity: 1; transform: scale(1.4); }
        }
        @keyframes statusFade {
          from { opacity: 0; transform: translateY(4px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes shimmer {
          0% { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
        @keyframes readyPop {
          from { transform: scale(0); }
          to { transform: scale(1); }
        }
        @keyframes checkDraw {
          to { stroke-dashoffset: 0; }
        }
      `}</style>

      {/* Skip button — subtle, bottom-right */}
      <button
        onClick={() => {
          setFading(true);
          setTimeout(() => {
            setVisible(false);
            onFinished?.();
          }, 300);
        }}
        style={{
          position: 'absolute',
          bottom: 32,
          right: 32,
          background: 'none',
          border: `1px solid ${palette.skipBorder}`,
          borderRadius: 6,
          color: palette.skipColor,
          fontSize: 12,
          fontWeight: 500,
          padding: '6px 16px',
          cursor: 'pointer',
          fontFamily: 'Inter, system-ui, sans-serif',
          letterSpacing: '0.5px',
          transition: 'all 0.2s ease',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.color = palette.skipHoverColor;
          e.currentTarget.style.borderColor = palette.skipHoverBorder;
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.color = palette.skipColor;
          e.currentTarget.style.borderColor = palette.skipBorder;
        }}
        aria-label="Skip intro animation"
      >
        Skip
      </button>
    </div>
  );
}
